import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { useParams, useNavigate } from 'react-router-dom';
import {
  TrophyIcon,
  ArrowLeftIcon,
  CalendarIcon,
  PlayIcon,
  CheckCircleIcon,
  ClockIcon,
  Loader2,
  ListOrderedIcon,
  LockIcon,
} from 'lucide-react';
import DailyExercisesHero from '../../components/student/dailyExercises/DailyExercisesHero';
import api from '../../services/api';
import toast from 'react-hot-toast';

const CompetitionDetails = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const { competitionId } = useParams();
  const navigate = useNavigate();

  const [competition, setCompetition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(null);
  const [countdownType, setCountdownType] = useState('start');

  const fetchCompetitionDetails = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/competitions/${competitionId}`);
      if (response.data.success) {
        setCompetition(response.data.data);
        localStorage.removeItem(`completed_rounds_${competitionId}`);
      }
    } catch (error) {
      console.error('Error fetching competition details:', error);
      toast.error(t('competitions.fetchError'));
      navigate('/student/competitions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompetitionDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitionId]);

  useEffect(() => {
    if (!competition?.schedule) return;

    const updateCountdown = () => {
      const now = new Date().getTime();
      const startTime = new Date(competition.schedule.competitionStart).getTime();
      const endTime = new Date(competition.schedule.competitionEnd).getTime();

      if (now < startTime) {
        const distance = startTime - now;
        setCountdownType('start');
        setCountdown(distance);
      } else if (now >= startTime && now < endTime) {
        const distance = endTime - now;
        setCountdownType('end');
        setCountdown(distance);
      } else {
        setCountdownType('ended');
        setCountdown(0);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [competition]);

  const formatCountdown = (milliseconds) => {
    if (!milliseconds || milliseconds <= 0) return '00:00:00:00';

    const days = Math.floor(milliseconds / (1000 * 60 * 60 * 24));
    const hours = Math.floor((milliseconds % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((milliseconds % (1000 * 60)) / 1000);

    return `${String(days).padStart(2, '0')}:${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active':
        return t('competitions.active');
      case 'upcoming':
      case 'scheduled':
        return t('competitions.upcoming');
      case 'completed':
        return t('competitions.completed');
      case 'cancelled':
        return t('competitions.cancelled');
      case 'draft':
        return t('competitions.draft');
      default:
        return status;
    }
  };

  const canPlayCompetition = () => {
    if (!competition) return false;
    if (competition.status === 'active') return true;
    if (competition.status === 'scheduled' && countdownType === 'end') return true;
    return false;
  };

  const visibleRounds = (competition?.rounds || []).filter((round, index) => {
    const roundNum = Number(round.roundNumber || index + 1);
    const limit = Number(competition?.totalRounds);
    if (!Number.isFinite(limit) || limit < 1) return true;
    return roundNum >= 1 && roundNum <= limit;
  });

  const renderRoundAction = (round, index) => {
    const roundNum = round.roundNumber || index + 1;
    const roundLabel = t('competitions.roundShort', { number: roundNum });
    const badgeClass = `inline-flex w-full min-h-[2.75rem] items-center justify-center gap-2 rounded-2xl border-[3px] px-4 py-2.5 text-xs font-bold sm:text-sm ${isRTL ? 'flex-row-reverse' : ''}`;

    if (round.isCompleted || round.myStatus === 'completed') {
      return (
        <span className={`${badgeClass} border-slate-200 bg-slate-100 text-slate-600`}>
          <CheckCircleIcon className="h-4 w-4 shrink-0" aria-hidden />
          <span>{roundLabel}</span>
          <span className="text-slate-500">· {t('competitions.roundCompleted')}</span>
        </span>
      );
    }

    if (round.myStatus === 'active') {
      return (
        <button
          type="button"
          onClick={() => startRound(roundNum)}
          disabled={!canPlayCompetition()}
          className={`${badgeClass} border-sky-400 bg-gradient-to-br from-sky-500 to-sky-600 text-white shadow-sm transition hover:from-sky-600 hover:to-sky-700 disabled:cursor-not-allowed disabled:opacity-50`}
        >
          <PlayIcon className="h-4 w-4 shrink-0" aria-hidden />
          <span>{roundLabel}</span>
        </button>
      );
    }

    if (round.canStart && canPlayCompetition()) {
      return (
        <button
          type="button"
          onClick={() => startRound(roundNum)}
          className={`${badgeClass} border-emerald-400 bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-sm transition hover:from-emerald-600 hover:to-emerald-700`}
        >
          <PlayIcon className="h-4 w-4 shrink-0" aria-hidden />
          <span>{roundLabel}</span>
        </button>
      );
    }

    if (round.canStart && !canPlayCompetition()) {
      return (
        <span className={`${badgeClass} border-slate-200 bg-slate-50 text-slate-500`}>
          <ClockIcon className="h-4 w-4 shrink-0" aria-hidden />
          <span>{roundLabel}</span>
          <span>· {countdownType === 'start' ? t('competitions.roundWaiting') : t('competitions.competitionNotOpen')}</span>
        </span>
      );
    }

    if (round.isLocked || round.myStatus === 'locked') {
      return (
        <span className={`${badgeClass} border-amber-200 bg-amber-50 text-amber-800`}>
          <LockIcon className="h-4 w-4 shrink-0" aria-hidden />
          <span>{roundLabel}</span>
          <span>· {t('competitions.roundLocked')}</span>
        </span>
      );
    }

    return (
      <span className={`${badgeClass} border-slate-200 bg-slate-50 text-slate-500`}>
        <ClockIcon className="h-4 w-4 shrink-0" aria-hidden />
        <span>{roundLabel}</span>
        <span>· {t('competitions.roundWaiting')}</span>
      </span>
    );
  };

  const statusBadgeClass = (status) => {
    if (status === 'active') return 'border-emerald-300 bg-emerald-50 text-emerald-900';
    if (status === 'upcoming' || status === 'scheduled') return 'border-sky-300 bg-sky-50 text-sky-900';
    if (status === 'completed') return 'border-slate-300 bg-slate-100 text-slate-800';
    if (status === 'cancelled') return 'border-rose-300 bg-rose-50 text-rose-900';
    return 'border-amber-300 bg-amber-50 text-amber-900';
  };

  const startRound = (roundNumber) => {
    navigate(`/student/competitions/${competitionId}/rounds/${roundNumber}`);
  };

  if (loading) {
    return (
      <div className="w-full" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="mx-auto max-w-6xl px-3 py-10 sm:px-4">
          <div className="rounded-[1.35rem] border-[3px] border-sky-200/80 bg-gradient-to-br from-white to-sky-50/80 px-6 py-14 text-center shadow-[0_8px_0_0_rgba(14,165,233,0.1)]">
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-sky-600" aria-hidden strokeWidth={2.25} />
            <p className="mt-4 text-sm font-bold text-slate-600 sm:text-base">{t('common.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!competition) {
    return (
      <div className="w-full px-3 py-10 sm:px-4" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="mx-auto max-w-6xl rounded-[1.35rem] border-[3px] border-slate-200/90 bg-white/95 px-6 py-14 text-center shadow-[0_8px_0_0_rgba(15,23,42,0.06)]">
          <p className="font-bold text-slate-700">{t('competitions.notFound')}</p>
        </div>
      </div>
    );
  }

  const heroDescription =
    competition.description?.trim() || t('competitions.studentPageDescription');

  const durationMs = Math.max(
    1,
    competition.schedule?.competitionEnd && competition.schedule?.competitionStart
      ? new Date(competition.schedule.competitionEnd).getTime() -
          new Date(competition.schedule.competitionStart).getTime()
      : 1
  );

  return (
    <div className="w-full pb-10" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="mx-auto w-full max-w-6xl space-y-5 px-3 pt-5 sm:space-y-6 sm:px-4 sm:pt-6">
        <div
          className={`flex flex-wrap items-center justify-between gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}
        >
          <button
            type="button"
            onClick={() => navigate('/student/competitions')}
            className={`inline-flex min-h-[2.5rem] items-center gap-2 rounded-2xl border-[3px] border-slate-900/10 bg-white px-4 text-sm font-bold text-slate-800 shadow-[0_4px_0_0_rgba(15,23,42,0.08)] transition hover:bg-sky-50 ${isRTL ? 'flex-row-reverse' : ''}`}
          >
            <ArrowLeftIcon className={`h-4 w-4 shrink-0 ${isRTL ? 'rotate-180' : ''}`} aria-hidden />
            {t('common.back')}
          </button>
          <div className={`flex flex-wrap items-center gap-2 sm:gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <span
              className={`inline-flex min-h-[2.5rem] items-center rounded-2xl border-[3px] px-4 text-xs font-black uppercase tracking-wide sm:text-sm ${statusBadgeClass(competition.status)}`}
            >
              {getStatusLabel(competition.status)}
            </span>
            <button
              type="button"
              onClick={() => navigate(`/leaderboard/${competitionId}`)}
              className={`inline-flex min-h-[2.5rem] items-center gap-2 rounded-2xl border-[3px] border-amber-300 bg-gradient-to-br from-amber-50 to-amber-100/80 px-4 text-sm font-bold text-amber-950 shadow-sm transition hover:from-amber-100 hover:to-amber-200/80 ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <TrophyIcon className="h-4 w-4 shrink-0 text-amber-700" aria-hidden />
              {t('competitions.competitionRanking')}
            </button>
          </div>
        </div>

        <DailyExercisesHero
          title={competition.name}
          description={heroDescription}
          icon={TrophyIcon}
          iconClassName="text-amber-600"
        />

        <section className="overflow-hidden rounded-[1.35rem] border-[3px] border-slate-900/10 bg-white/95 shadow-[0_8px_0_0_rgba(15,23,42,0.06)]">
          <div className="border-b border-slate-200/90 bg-gradient-to-r from-sky-50/90 via-white to-violet-50/40 px-4 py-3 sm:px-5">
            <h2 className="text-sm font-black tracking-tight text-slate-900 sm:text-base">
              {t('competitions.competitionDetails')}
            </h2>
          </div>
          <div className="grid gap-4 p-4 sm:grid-cols-3 sm:gap-5 sm:p-5">
            <div className="flex items-center gap-3 rounded-2xl border-[3px] border-sky-200/80 bg-gradient-to-br from-sky-50/80 to-white px-4 py-3 shadow-sm">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border-[3px] border-sky-200 bg-white">
                <ListOrderedIcon className="h-5 w-5 text-sky-700" aria-hidden />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
                  {t('competitions.totalRounds')}
                </p>
                <p className="text-2xl font-black tabular-nums text-slate-900">{competition.totalRounds}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-2xl border-[3px] border-violet-200/80 bg-gradient-to-br from-violet-50/60 to-white px-4 py-3 shadow-sm">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border-[3px] border-violet-200 bg-white">
                <CalendarIcon className="h-5 w-5 text-violet-700" aria-hidden />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
                  {t('competitions.registrationStart')}
                </p>
                <p className="text-sm font-bold leading-snug text-slate-900">
                  {formatDate(competition.schedule?.registrationStart)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-2xl border-[3px] border-amber-200/80 bg-gradient-to-br from-amber-50/70 to-white px-4 py-3 shadow-sm">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border-[3px] border-amber-200 bg-white">
                <ClockIcon className="h-5 w-5 text-amber-700" aria-hidden />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
                  {t('competitions.competitionStart')}
                </p>
                <p className="text-sm font-bold leading-snug text-slate-900">
                  {formatDate(competition.schedule?.competitionStart)}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="overflow-hidden rounded-[1.35rem] border-[3px] border-slate-900/10 bg-white/95 shadow-[0_8px_0_0_rgba(15,23,42,0.06)]">
          <div
            className={`flex flex-col gap-4 border-b border-slate-200/90 bg-gradient-to-r from-amber-50/90 via-white to-emerald-50/40 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5 ${isRTL ? 'sm:flex-row-reverse' : ''}`}
          >
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <h2 className="text-base font-black tracking-tight text-slate-900 sm:text-lg">{t('competitions.rounds')}</h2>
              <p className="mt-0.5 text-sm font-semibold text-slate-600">{t('competitions.manageRounds')}</p>
            </div>

            {countdown !== null && (
              <div className="text-center sm:min-w-[220px]">
                <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
                  {countdownType === 'start'
                    ? t('competitions.startsIn')
                    : countdownType === 'end'
                      ? t('competitions.endsIn')
                      : t('competitions.ended')}
                </p>
                <div
                  className={`mt-1 font-mono text-xl font-black tabular-nums transition-colors duration-500 sm:text-2xl ${
                    countdownType === 'start'
                      ? 'text-emerald-700'
                      : countdownType === 'end'
                        ? countdown / durationMs > 0.7
                          ? 'text-emerald-700'
                          : countdown / durationMs > 0.5
                            ? 'text-amber-600'
                            : countdown / durationMs > 0.3
                              ? 'text-orange-600'
                              : 'text-rose-600'
                        : 'text-slate-500'
                  }`}
                >
                  {formatCountdown(countdown)}
                </div>
                <div className="mt-1 flex justify-center gap-3 text-[10px] font-semibold text-slate-500">
                  <span>{t('common.days')}</span>
                  <span>{t('common.hours')}</span>
                  <span>{t('common.minutes')}</span>
                  <span>{t('common.seconds')}</span>
                </div>
              </div>
            )}
          </div>

          <div className="p-4 sm:p-5">
            {visibleRounds.length > 0 ? (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {visibleRounds.map((round, index) => (
                  <div key={round.roundNumber ?? index}>{renderRoundAction(round, index)}</div>
                ))}
              </div>
            ) : (
              <div className="px-2 py-14 text-center">
                <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-2xl border-[3px] border-slate-200 bg-slate-50">
                  <TrophyIcon className="h-8 w-8 text-slate-300" aria-hidden />
                </div>
                <p className="font-bold text-slate-600">{t('competitions.noRounds')}</p>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default CompetitionDetails;
